// Everything the website knows about the Cinescape apps, in one place: the
// hosts the apps claim, how to open them, and where to get them.
// docs/architecture/app-links-tracking-journeys.md §1.1 and §6.
//
// config.js loads before the site's code and may override any value below
// for the environment it is served on, for example:
//
//   window.CINESCAPE_APP_LINKS = {
//     bannerEnabled: true,
//     claimedHosts: ["www.cinescape.com.kw", "test.cinescape.com.kw"],
//   };
//
// A value that is missing, or of the wrong type, keeps its default below.
//
// WHERE AN OVERRIDE LASTS. On the test server the Deploy workflow writes
// config.js afresh at every deployment (.github/workflows/deploy-test-server.yml, the
// website job), so a lasting value for the test server goes there; it turns
// bannerEnabled and testServer on. Every other environment is built by Bitbucket and serves
// this repository's public/config.js, the same file everywhere and replaced
// at every release, so an edit made on one of those servers lasts only until
// the next release. A lasting change there is a change to the defaults below,
// and a release.
//
// KEEP IN STEP WITH three other places, which the tests check:
//   - public/.well-known/apple-app-site-association and assetlinks.json name
//     the same app identifiers (applinks.test.js, scripts/check-app-links.mjs);
//   - the Smart App Banner script in index.html and properties/*/index.html
//     repeats bannerEnabled, testServer, the default hosts, scheme and App
//     Store id,
//     because it runs before this file has loaded (applinks.test.js runs it
//     and compares).

export const APP_LINK_DEFAULTS = Object.freeze({
  // Whether the website offers the app at all: the "Open in the Cinescape
  // app" banner, Safari's Smart App Banner, and the Open button on the "Open
  // in the app" pages (whose store buttons show either way). OFF until the
  // app versions that handle these links are in the stores. The apps people
  // have installed today register no cinescape:// scheme and claim no https
  // links, so an Open button would send an Android visitor who has the app to
  // the Play Store, and do nothing on an iPhone. The test server turns it on
  // in the config.js the Deploy workflow writes. Only true turns it on.
  bannerEnabled: false,

  // True only on the test servers, whose config.js the Deploy workflow and
  // AWS deploy write. There Android's Play Store fallback and Safari's Smart
  // App Banner would install the LIVE app, which talks to the live backend,
  // while testers need the test builds. So an Android Open link has no
  // fallback, Safari's banner is not written (the site's own banner shows in
  // its place), and a short link opens the app rather than leaving for the
  // live website. The store buttons (footer and "Open in the app" pages) show
  // here too, as on the live site. Only true turns it on.
  testServer: false,

  // §1.1. Only a page served over https from one of these can hand a link to
  // the app as an https address. Anywhere else (an IP address on the office
  // network, localhost) universal links and app links cannot be verified, so
  // the website uses cinescape:// instead.
  claimedHosts: Object.freeze([
    "www.cinescape.com.kw",
    "cinescape.com.kw",
    "open.cinescape.com.kw",
    "uatweb.cinescape.com.kw",
  ]),

  // iOS will not switch to the app for a link that stays on the page's own
  // host, so the banner on iPhone browsers other than Safari points here. It
  // needs a DNS record and a certificate first (deploy/open-host.md). Until
  // openHostLive is true the banner uses cinescape:// instead.
  openHost: "open.cinescape.com.kw",
  openHostLive: false,

  scheme: "cinescape",

  iosTeamId: "SDQA7SJ24D",
  iosBundleId: "com.kncc.cinescape",
  iosAppStoreId: "443396586",
  appStoreUrl: "https://apps.apple.com/app/id443396586",

  androidPackage: "com.cinescape",
  playStoreUrl: "https://play.google.com/store/apps/details?id=com.cinescape",
});

const HOST_KEYS = new Set(["claimedHosts", "openHost"]);

export function resolveAppLinkConfig(overrides) {
  const config = { ...APP_LINK_DEFAULTS };
  if (!overrides || typeof overrides !== "object") return Object.freeze(config);

  for (const key of Object.keys(APP_LINK_DEFAULTS)) {
    const fallback = APP_LINK_DEFAULTS[key];
    const value = overrides[key];

    if (Array.isArray(fallback)) {
      const usable =
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => typeof v === "string" && v.trim() !== "");
      if (usable) {
        config[key] = Object.freeze(
          value.map((v) => (HOST_KEYS.has(key) ? v.trim().toLowerCase() : v.trim()))
        );
      }
    } else if (typeof fallback === "string") {
      if (typeof value === "string" && value.trim() !== "") {
        config[key] = HOST_KEYS.has(key) ? value.trim().toLowerCase() : value.trim();
      }
    } else if (typeof value === typeof fallback) {
      config[key] = value;
    }
  }
  return Object.freeze(config);
}

export const APP_LINKS = resolveAppLinkConfig(
  typeof window !== "undefined" ? window.CINESCAPE_APP_LINKS : undefined
);
